/**
 * CSRF Token Manager
 *
 * SECURITY: VULN-001 FIX
 * Manages CSRF tokens for protecting against Cross-Site Request Forgery attacks.
 *
 * How CSRF Protection Works:
 * 1. Backend generates a unique CSRF token and sends it via cookie (csrf_token)
 * 2. Frontend reads the token from the cookie
 * 3. Frontend includes the token in X-CSRF-Token header for state-changing requests
 * 4. Backend validates the token matches for POST/PUT/DELETE/PATCH requests
 *
 * IMPORTANT: Backend Implementation Required
 * For CSRF protection to work, the backend MUST:
 * - Generate CSRF tokens and set them in a cookie named 'csrf_token'
 * - Validate the X-CSRF-Token header matches the cookie value
 * - Reject state-changing requests without valid CSRF tokens
 *
 * Cookie Requirements:
 * - Name: csrf_token
 * - Path: /
 * - SameSite: Strict or Lax (Strict preferred for security)
 * - HttpOnly: false (must be readable by JavaScript)
 * - Secure: true (in production with HTTPS)
 *
 * Reference: OWASP CSRF Prevention Cheat Sheet
 * https://cheatsheetseries.owasp.org/cheatsheets/Cross-Site_Request_Forgery_Prevention_Cheat_Sheet.html
 */

import { log } from '../errors/logger';

/**
 * CSRF token cookie name
 * This should match the cookie name used by the backend
 */
const CSRF_COOKIE_NAME = 'csrf_token';

/**
 * CSRF token header name
 * This is the standard header name for CSRF tokens
 */
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * Get CSRF token from cookie
 *
 * Reads the CSRF token from the cookie set by the backend.
 * This token will be included in request headers for state-changing operations.
 *
 * @returns {string | null} The CSRF token if available, null otherwise
 */
export function getCsrfToken(): string | null {
  try {
    // Parse all cookies from document.cookie
    const cookies = document.cookie.split(';');

    // Find the CSRF token cookie
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === CSRF_COOKIE_NAME) {
        const token = decodeURIComponent(value);

        // Validate token format (should be non-empty string)
        if (token && token.length > 0) {
          return token;
        }

        log.warn('CSRF token found but empty or invalid', { tokenLength: token?.length });
        return null;
      }
    }

    // Token not found
    log.debug('CSRF token cookie not found', { cookieName: CSRF_COOKIE_NAME });
    return null;

  } catch (error) {
    log.error('Error reading CSRF token from cookie', { error });
    return null;
  }
}

/**
 * Check if CSRF token is available
 *
 * Use this to check if CSRF protection is enabled before making requests.
 * If no token is available, the backend may not have CSRF protection configured yet.
 *
 * @returns {boolean} True if CSRF token is available
 */
export function hasCsrfToken(): boolean {
  return getCsrfToken() !== null;
}

/**
 * Get CSRF token with fallback handling
 *
 * This function logs a warning if CSRF token is missing.
 * Used in HTTP clients to gracefully handle missing tokens during development
 * or when backend hasn't implemented CSRF protection yet.
 *
 * @param {boolean} warnOnMissing - Whether to log a warning if token is missing (default: true)
 * @returns {string | null} The CSRF token or null
 */
export function getCsrfTokenWithWarning(warnOnMissing: boolean = true): string | null {
  const token = getCsrfToken();

  if (!token && warnOnMissing) {
    log.warn(
      'CSRF token not found - Backend may not have CSRF protection configured. ' +
      'State-changing requests (POST/PUT/DELETE/PATCH) are vulnerable to CSRF attacks. ' +
      'See CSRF documentation for backend implementation requirements.'
    );
  }

  return token;
}

/**
 * Get CSRF headers object for fetch requests
 *
 * Returns an object with the CSRF token header if available.
 * Can be spread into fetch headers: { ...headers, ...getCsrfHeaders() }
 *
 * @param {boolean} warnOnMissing - Whether to log a warning if token is missing (default: false)
 * @returns {Record<string, string>} Headers object with CSRF token (empty if not available)
 */
export function getCsrfHeaders(warnOnMissing: boolean = false): Record<string, string> {
  const token = getCsrfTokenWithWarning(warnOnMissing);

  if (token) {
    return {
      [CSRF_HEADER_NAME]: token
    };
  }

  return {};
}

/**
 * Check if HTTP method requires CSRF protection
 *
 * CSRF protection is required for state-changing operations.
 * GET, HEAD, and OPTIONS are considered safe methods and don't need CSRF protection.
 *
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, PATCH, etc.)
 * @returns {boolean} True if method requires CSRF protection
 */
export function requiresCsrfToken(method: string): boolean {
  const safeMethod = method.toUpperCase();
  const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
  return !safeMethods.includes(safeMethod);
}
